import { useEffect, useCallback, useState } from 'react';
import * as Sentry from '@sentry/nextjs';

import FridgeDoorStore from '@/stores/FridgeDoorStore';
import ProfileBuilderStore from '@/stores/ProfileBuilderStore';
import {
  ILifeEventSubscriptionGroups,
  TLifeEventType,
} from '@/types/ProfileBuilder/profileBuilder';

type TLifeEvent = {
  busy: boolean;
  lifeEvent: TLifeEventType;
  lifeEventSubscriptionGroups: ILifeEventSubscriptionGroups[];
};

export const useLifeEvent = (): TLifeEvent => {
  const { currentVillageId, villages, allVillagesLoading } = FridgeDoorStore;

  const [lifeEvent, setLifeEvent] = useState<TLifeEventType | null>(null);
  const [
    lifeEventSubscriptionGroups,
    setLifeEventSubscriptionGroups,
  ] = useState<ILifeEventSubscriptionGroups[] | null>(null);
  const [busy, setBusy] = useState(false);

  const getLifeEventData = useCallback(async () => {
    setBusy(true);
    try {
      const currentLifeEventId = villages?.find(
        (v) => v.slug === currentVillageId,
      ).life_event_type;

      await ProfileBuilderStore.setLifeEvent(currentLifeEventId);
      await ProfileBuilderStore.getLifeEventSubscriptionGroups(
        currentLifeEventId,
      );

      setLifeEvent(ProfileBuilderStore.lifeEvent);
      setLifeEventSubscriptionGroups(
        ProfileBuilderStore.lifeEventSubscriptionGroups,
      );
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setBusy(false);
    }
  }, [currentVillageId, villages]);

  const getMockData = useCallback(async () => {
    const lifeEventTypes = await ProfileBuilderStore.getLifeEventTypes();
    const randomLifeEvent =
      lifeEventTypes[Math.floor(Math.random() * lifeEventTypes.length)];

    if (randomLifeEvent) {
      await ProfileBuilderStore.setLifeEvent(randomLifeEvent.id);

      await ProfileBuilderStore.getLifeEventSubscriptionGroups(
        randomLifeEvent.id,
      );
    }

    setLifeEvent(ProfileBuilderStore.lifeEvent);
    if (ProfileBuilderStore.lifeEventSubscriptionGroups?.length)
      setLifeEventSubscriptionGroups(
        ProfileBuilderStore.lifeEventSubscriptionGroups.slice(0, 20),
      );
  }, []);

  useEffect(() => {
    if (!villages && !allVillagesLoading) {
      getMockData();
    } else {
      getLifeEventData();
    }
    return () => {
      setLifeEvent(null);
    };
  }, [getLifeEventData, getMockData, villages, allVillagesLoading]);

  return { busy, lifeEvent, lifeEventSubscriptionGroups };
};
